import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Button, Typography, Container, Stack, Paper, Grid, Card, CardContent,
    CardMedia, Divider, Chip, Avatar, Tooltip, List, ListItem, ListItemIcon,
    ListItemText, useTheme, useMediaQuery
} from '@mui/material';

// Icons
import RecyclingIcon from '@mui/icons-material/Recycling';
import UploadIcon from '@mui/icons-material/Upload';
import SchoolIcon from '@mui/icons-material/School';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NatureIcon from '@mui/icons-material/Nature';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DevicesIcon from '@mui/icons-material/Devices';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import { useAuth } from '../contexts/AuthContext';
import ImpactStats from '../components/layout/Footer/ImpactStats';
import HomeTipSection from '../components/edutips/HomeTipSection';
import ImpactTagline from '../components/common/ImpactTagline';
import { useFirstTimeVisitor } from '../hooks/useFirstTimeVisitor';

export default function LandingPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isAuthenticated, user } = useAuth();
    const {
        shouldShowTutorial,
        landingTutorialCompleted,
        markTutorialCompleted,
        markLandingTutorialCompleted
    } = useFirstTimeVisitor();

    const handleTutorialComplete = () => {
        markTutorialCompleted();
        markLandingTutorialCompleted();
    };

    const handleTutorialSkip = () => {
        markLandingTutorialCompleted();
    };

    // Environmental impact stats
    const impactStats = [
        { value: '85%', label: 'Accuracy', description: 'In waste classification' },
        { value: '40%', label: 'Reduction', description: 'In improper disposal' },
        { value: '1,200+', label: 'Users', description: 'And growing every day' },
        { value: '15K+', label: 'Items Sorted', description: 'Making a difference' }
    ];

    // Waste categories
    const wasteCategories = [
        { name: 'Recyclable', color: '#2196f3', icon: <RecyclingIcon /> },
        { name: 'Organic', color: '#4caf50', icon: <NatureIcon /> },
        { name: 'Hazardous', color: '#f44336', icon: <BarChartIcon /> },
        { name: 'Electronic', color: '#9c27b0', icon: <DevicesIcon /> }
    ];

    return (
        <>
            {/* Hero Section with Mobile-First Responsive Design */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, rgba(230, 244, 234, 0.9) 0%, rgba(212, 239, 223, 0.9) 100%)`,
                    py: { xs: 6, sm: 8, md: 12, lg: 16 },
                    display: "flex",
                    alignItems: "center",
                    minHeight: { xs: 'auto', md: '70vh' },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/images/pattern-bg.svg)',
                        backgroundSize: 'cover',
                        opacity: 0.05,
                        zIndex: 0
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} alignItems="center">
                        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                            <Box
                                data-aos="fade-right"
                                data-aos-delay="100"
                                data-aos-duration="1000"
                                sx={{ textAlign: { xs: 'center', md: 'left' } }}
                            >
                                <Chip
                                    label="AI-Powered Waste Management"
                                    color="primary"
                                    size={isSmallMobile ? "small" : "medium"}
                                    sx={{
                                        mb: { xs: 2, sm: 3 },
                                        fontWeight: 600,
                                        background: 'linear-gradient(90deg, #43a047 30%, #66bb6a 90%)',
                                        '& .MuiChip-label': { px: { xs: 1.5, sm: 2 } },
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                />
                                <Typography
                                    variant="h1"
                                    fontWeight={800}
                                    color="primary"
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                                        backgroundImage: 'linear-gradient(90deg, #1b5e20, #43a047)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        mb: { xs: 1, sm: 2 },
                                        textShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                                        lineHeight: { xs: 1.1, sm: 1.2 }
                                    }}
                                >
                                    Sortify
                                </Typography>
                                <Typography
                                    variant="h4"
                                    color="text.secondary"
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem', lg: '2rem' },
                                        fontWeight: 600,
                                        mb: { xs: 1.5, sm: 2 },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    Your Smart Waste Buddy
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    paragraph
                                    sx={{
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                        lineHeight: { xs: 1.5, sm: 1.6 },
                                        maxWidth: { xs: '100%', sm: '90%', md: '100%' },
                                        mx: { xs: 'auto', md: 0 }
                                    }}
                                >
                                    Take a photo of your waste and instantly discover the proper disposal method.
                                    Join us in creating a cleaner, greener Bangladesh through AI-powered waste sorting.
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 2, sm: 3 }}
                                    sx={{
                                        mb: { xs: 4, md: 0 },
                                        alignItems: { xs: 'stretch', sm: 'center' }
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        color="primary"
                                        component={Link}
                                        to="/classify"
                                        startIcon={<CloudUploadIcon />}
                                        data-tutorial="classify-button"
                                        sx={{
                                            fontWeight: 600,
                                            px: { xs: 3, sm: 4 },
                                            py: { xs: 1.2, sm: 1.5 },
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' },
                                            borderRadius: { xs: 2, sm: 1 }
                                        }}
                                    >
                                        {isSmallMobile ? 'Classify Now' : 'Classify Waste Now'}
                                    </Button>
                                    {!isAuthenticated ? (
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            component={Link}
                                            to="/login"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.2, sm: 1.5 },
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                borderRadius: { xs: 2, sm: 1 },
                                                '&:hover': { borderWidth: 2 }
                                            }}
                                        >
                                            {isSmallMobile ? 'Login' : 'Login / Register'}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            component={Link}
                                            to="/profile"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.2, sm: 1.5 },
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                borderRadius: { xs: 2, sm: 1 },
                                                '&:hover': { borderWidth: 2 }
                                            }}
                                        >
                                            {isSmallMobile ? 'Profile' : 'View Your Profile'}
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            {/* Mobile-Optimized Hero Illustration */}
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: { xs: 300, sm: 400, md: 500 },
                                    height: { xs: 250, sm: 300, md: 400 },
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative'
                                }}
                            >
                                {/* Main illustration container */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative',
                                        borderRadius: { xs: 4, sm: 6, md: 8 },
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(135deg, #e6f4ea 0%, #d4efdf 100%)',
                                            opacity: 0.8,
                                            zIndex: 0
                                        }
                                    }}
                                >
                                    <RecyclingIcon
                                        sx={{
                                            fontSize: { xs: 120, sm: 150, md: 180 },
                                            color: '#43a047',
                                            opacity: 0.9,
                                            filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
                                            zIndex: 1
                                        }}
                                    />
                                </Box>

                                {/* Animated floating waste items - Mobile responsive */}
                                {wasteCategories.map((category, index) => (
                                    <Tooltip key={index} title={category.name} arrow>
                                        <Avatar
                                            sx={{
                                                position: 'absolute',
                                                backgroundColor: category.color,
                                                width: { xs: 35, sm: 45, md: 50 },
                                                height: { xs: 35, sm: 45, md: 50 },
                                                top: `${20 + (index * 25)}%`,
                                                left: `${10 + (index * 20)}%`,
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                animation: `float ${2 + (index * 0.5)}s ease-in-out infinite alternate`,
                                                '@keyframes float': {
                                                    '0%': { transform: 'translateY(0px)' },
                                                    '100%': { transform: 'translateY(-10px)' }
                                                },
                                                zIndex: 2,
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: { xs: 18, sm: 22, md: 24 }
                                                }
                                            }}
                                        >
                                            {category.icon}
                                        </Avatar>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Impact Tagline Section - Mobile Optimized */}
           <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, textAlign: 'center' }}>
               <ImpactTagline variant="hero" animated={true} />
           </Container>

            {/* How It Works Section - Mobile Responsive */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 10 } }} id="how-it-works">
                <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
                    <Chip
                        label="Simple Process"
                        color="primary"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                    />
                    <Typography
                        variant="h3"
                        component="h2"
                        fontWeight={700}
                        gutterBottom
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        How Sortify Works
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            maxWidth: { xs: '100%', sm: 600, md: 700 },
                            mx: 'auto',
                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                            px: { xs: 2, sm: 0 }
                        }}
                    >
                        Our AI-powered platform makes waste sorting simple, educational, and effective.
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <Box sx={{ position: 'relative', height: '100%', minHeight: { xs: 300, sm: 400, md: 460 } }}>
                            {/* Background decorative element - Hidden on small mobile */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: { xs: '90%', md: '80%' },
                                    height: '75%',
                                    background: 'linear-gradient(135deg, #e6f4ea 0%, #d4efdf 100%)',
                                    borderRadius: { xs: 4, sm: 6 },
                                    left: 0,
                                    top: 0,
                                    zIndex: 1,
                                    boxShadow: '0 10px 40px rgba(67, 160, 71, 0.07)',
                                    display: { xs: 'none', sm: 'block' }
                                }}
                            />

                            {/* Result Card - Mobile Optimized */}
                            <Paper
                                elevation={4}
                                sx={{
                                    position: 'relative',
                                    width: { xs: '100%', md: '85%' },
                                    minHeight: { xs: 300, sm: 380, md: 420 },
                                    borderRadius: { xs: 3, sm: 4, md: 5 },
                                    right: { xs: 0, md: 0 },
                                    bottom: { xs: 0, md: 0 },
                                    mt: { xs: 0, md: '15%' },
                                    ml: { xs: 0, md: '15%' },
                                    zIndex: 2,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: '0 15px 50px rgba(67, 160, 71, 0.15)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {/* Header Bar - Mobile Responsive */}
                                <Box
                                    sx={{
                                        py: { xs: 1.5, sm: 2 },
                                        px: { xs: 2, sm: 3 },
                                        background: 'linear-gradient(90deg, #43a047, #66bb6a)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Typography
                                        color="white"
                                        fontWeight={600}
                                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}
                                    >
                                        {isSmallMobile ? 'Classification Result' : 'Waste Classification Result'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {['#ffffff', '#ffffff', '#ffffff'].map((color, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    width: { xs: 4, sm: 6 },
                                                    height: { xs: 4, sm: 6 },
                                                    borderRadius: '50%',
                                                    bgcolor: color,
                                                    opacity: 0.7 + (i * 0.1)
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Content Area - Mobile Optimized */}
                                <Box sx={{
                                    p: { xs: 2, sm: 3, md: 4 },
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    {/* Result Badge and Title - Mobile Layout */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: { xs: 2, sm: 3 },
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: '#00b894',
                                                width: { xs: 50, sm: 60, md: 70 },
                                                height: { xs: 50, sm: 60, md: 70 },
                                                mr: { xs: 0, sm: 2 },
                                                mb: { xs: 1.5, sm: 0 },
                                                boxShadow: '0 4px 14px rgba(0, 184, 148, 0.2)'
                                            }}
                                        >
                                            <RecyclingIcon sx={{
                                                fontSize: { xs: 24, sm: 32, md: 40 },
                                                color: 'white'
                                            }} />
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                fontWeight={700}
                                                color="primary.dark"
                                                gutterBottom
                                                sx={{
                                                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' }
                                                }}
                                            >
                                                Recyclable - Paper
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                                flexDirection: { xs: 'column', sm: 'row' }
                                            }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontWeight={500}
                                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                >
                                                    Confidence:
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{
                                                        width: { xs: 80, sm: 100 },
                                                        height: { xs: 4, sm: 6 },
                                                        bgcolor: '#e0e0e0',
                                                        borderRadius: 3,
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Box sx={{
                                                            width: '96%',
                                                            height: '100%',
                                                            bgcolor: '#00b894',
                                                            borderRadius: 3
                                                        }} />
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="primary.dark"
                                                        fontWeight={700}
                                                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                                    >
                                                        96%
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Divider */}
                                    <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

                                    {/* Disposal Instructions - Mobile Optimized */}
                                    <Typography
                                        variant="h6"
                                        color="primary.dark"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 600,
                                            mb: { xs: 1.5, sm: 2 },
                                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                                        }}
                                    >
                                        How to dispose:
                                    </Typography>

                                    <Box sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        bgcolor: 'rgba(67, 160, 71, 0.08)',
                                        borderRadius: 2,
                                        borderLeft: '4px solid',
                                        borderColor: 'primary.main',
                                        mb: { xs: 2, sm: 3 }
                                    }}>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                            fontWeight={500}
                                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                        >
                                            Place in your blue recycling bin.
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                        >
                                            Paper products should be clean and dry. Remove any plastic wrapping or tape before recycling.
                                        </Typography>
                                    </Box>

                                    {/* Status Badge - Mobile Layout */}
                                    <Box sx={{
                                        mt: 'auto',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: { xs: 1.5, sm: 0 }
                                    }}>
                                        <Chip
                                            icon={<CheckCircleIcon />}
                                            label={isSmallMobile ? "Added to history" : "Added to your sorting history"}
                                            color="success"
                                            variant="outlined"
                                            size={isSmallMobile ? "small" : "medium"}
                                            sx={{
                                                fontWeight: 500,
                                                borderWidth: 1.5,
                                                borderColor: 'success.main',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                '& .MuiChip-icon': {
                                                    color: 'success.main'
                                                }
                                            }}
                                        />
                                        <Button
                                            size="small"
                                            color="primary"
                                            endIcon={<ArrowForwardIcon fontSize="small" />}
                                            sx={{
                                                fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                            }}
                                        >
                                            See History
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                        <Box sx={{ pl: { md: 4 } }}>
                            <List>
                                {[
                                    {
                                        icon: <CloudUploadIcon color="primary" fontSize="large" />,
                                        title: "Upload a Photo",
                                        description: "Take a picture or upload an image of your waste item using our intuitive interface."
                                    },
                                    {
                                        icon: <AutoAwesomeIcon color="primary" fontSize="large" />,
                                        title: "AI Analysis",
                                        description: "Our advanced AI model analyzes the image to identify the waste type and proper disposal method."
                                    },
                                    {
                                        icon: <CheckCircleIcon color="primary" fontSize="large" />,
                                        title: "Get Clear Instructions",
                                        description: "Receive specific guidance on how to dispose of your waste item correctly."
                                    },
                                    {
                                        icon: <SchoolIcon color="primary" fontSize="large" />,
                                        title: "Learn & Improve",
                                        description: "Access educational tips and track your progress over time to become a waste sorting expert."
                                    }
                                ].map((step, index) => (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            pb: { xs: 2, sm: 3 },
                                            px: { xs: 1, sm: 2 },
                                            position: 'relative',
                                            '&:after': index < 3 ? {
                                                content: '""',
                                                position: 'absolute',
                                                left: { xs: 24, sm: 28 },
                                                top: { xs: 50, sm: 60 },
                                                height: { xs: 20, sm: 30 },
                                                width: 2,
                                                bgcolor: 'primary.light',
                                                opacity: 0.5
                                            } : {}
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: { xs: 48, sm: 56 },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: { xs: 32, sm: 40 },
                                                    filter: 'drop-shadow(0px 2px 4px rgba(67, 160, 71, 0.2))'
                                                }
                                            }}
                                        >
                                            {step.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="h6"
                                                    fontWeight={600}
                                                    gutterBottom
                                                    sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                                                >
                                                    {step.title}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                                >
                                                    {step.description}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* Educational Tip Section */}
            <Container maxWidth="lg">
                <HomeTipSection />
            </Container>

            {/* Environmental Impact Stats */}
            <ImpactStats variant="section" />

            {/* Features Section - Mobile Responsive */}
            <Box
                sx={{
                    py: { xs: 6, sm: 8, md: 10 },
                    background: 'linear-gradient(135deg, rgba(230, 244, 234, 0.4) 0%, rgba(212, 239, 223, 0.4) 100%)'
                }}
                id="features"
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
                        <Chip
                            label="Key Capabilities"
                            color="primary"
                            size={isSmallMobile ? "small" : "medium"}
                            sx={{
                                mb: 2,
                                fontWeight: 600,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                        />
                        <Typography
                            variant="h3"
                            component="h2"
                            fontWeight={700}
                            gutterBottom
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                                lineHeight: { xs: 1.2, sm: 1.3 }
                            }}
                        >
                            Smart Waste Management Made Easy
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            paragraph
                            sx={{
                                mb: { xs: 4, sm: 6 },
                                maxWidth: { xs: '100%', sm: 600, md: 700 },
                                mx: 'auto',
                                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            Sortify uses advanced AI to help you make environmentally responsible waste disposal decisions.
                        </Typography>

                        <Grid container spacing={{ xs: 3, sm: 4 }}>
                            {[
                                {
                                    title: "Instant Classification",
                                    description: "Upload a photo of your waste item and get immediate sorting recommendations backed by our custom-trained AI model.",
                                    icon: <UploadIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: "#388e3c" }} />
                                },
                                {
                                    title: "Educational Tips",
                                    description: "Learn proper waste management techniques and environmental best practices tailored to your local recycling guidelines.",
                                    icon: <SchoolIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: "#388e3c" }} />
                                },
                                {
                                    title: "Track Progress",
                                    description: "Monitor your environmental impact and improve your waste sorting habits with detailed analytics and history tracking.",
                                    icon: <BarChartIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: "#388e3c" }} />
                                },
                                {
                                    title: "Sustainability Focus",
                                    description: "Join our mission to create a cleaner Bangladesh through proper waste management and community engagement.",
                                    icon: <RecyclingIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: "#388e3c" }} />
                                }
                            ].map((feature, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <Card
                                        elevation={2}
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                            p: { xs: 2, sm: 3 },
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: { xs: 'none', sm: 'translateY(-4px)' },
                                                boxShadow: { xs: 2, sm: 4 }
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mb: { xs: 2, sm: 3 },
                                                '& .MuiSvgIcon-root': {
                                                    filter: 'drop-shadow(0px 4px 6px rgba(67, 160, 71, 0.2))'
                                                }
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <CardContent sx={{ flexGrow: 1, p: 0 }}>
                                            <Typography
                                                variant="h6"
                                                component="h3"
                                                fontWeight={600}
                                                gutterBottom
                                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    lineHeight: 1.6,
                                                    fontSize: { xs: '0.85rem', sm: '0.875rem' }
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* Tech Stack Section - Mobile Responsive */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 10 } }}>
                <Grid container spacing={{ xs: 4, sm: 6 }} alignItems="center">
                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                            <Chip
                                label="Technology"
                                color="primary"
                                size={isSmallMobile ? "small" : "medium"}
                                sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                            />
                            <Typography
                                variant="h3"
                                component="h2"
                                fontWeight={700}
                                gutterBottom
                                sx={{
                                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                                    lineHeight: { xs: 1.2, sm: 1.3 }
                                }}
                            >
                                Built with Modern Technology
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                paragraph
                                sx={{
                                    mb: { xs: 3, sm: 4 },
                                    lineHeight: 1.6,
                                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                                    maxWidth: { xs: '100%', md: '90%' }
                                }}
                            >
                                Sortify combines cutting-edge technologies to deliver a seamless, reliable, and intelligent waste classification experience.
                            </Typography>

                            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                                {[
                                    {
                                        title: "React Frontend",
                                        description: "Responsive and interactive UI built with React and Material UI",
                                        icon: <CodeIcon />
                                    },
                                    {
                                        title: "Spring Boot Backend",
                                        description: "Robust Java backend with RESTful APIs and efficient data handling",
                                        icon: <StorageIcon />
                                    },
                                    {
                                        title: "TensorFlow AI",
                                        description: "Custom-trained machine learning model for accurate waste classification",
                                        icon: <AutoAwesomeIcon />
                                    }
                                ].map((tech, index) => (
                                    <Grid item xs={12} sm={6} md={12} key={index}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            mb: 1,
                                            textAlign: { xs: 'center', sm: 'left' },
                                            flexDirection: { xs: 'column', sm: 'row' }
                                        }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    width: { xs: 36, sm: 40 },
                                                    height: { xs: 36, sm: 40 },
                                                    mr: { xs: 0, sm: 2 },
                                                    mb: { xs: 1, sm: 0 },
                                                    mx: { xs: 'auto', sm: 0 }
                                                }}
                                            >
                                                {tech.icon}
                                            </Avatar>
                                            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                                <Typography
                                                    variant="h6"
                                                    fontWeight={600}
                                                    gutterBottom
                                                    sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                                                >
                                                    {tech.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                                                >
                                                    {tech.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>

                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    component={Link}
                                    to="/about"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        fontWeight: 600,
                                        borderWidth: 2,
                                        px: { xs: 3, sm: 4 },
                                        py: { xs: 1, sm: 1.2 },
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        '&:hover': { borderWidth: 2 }
                                    }}
                                >
                                    {isSmallMobile ? 'Learn More' : 'Learn More About Our Technology'}
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                        <Paper
                            elevation={4}
                            sx={{
                                borderRadius: { xs: 2, sm: 4 },
                                overflow: 'hidden',
                                height: '100%',
                                minHeight: { xs: 250, sm: 350, md: 400 },
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: 'background.default',
                                    p: { xs: 1.5, sm: 2 },
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: { xs: 0.5, sm: 0.7 },
                                        mr: { xs: 1, sm: 2 }
                                    }}
                                >
                                    {['#ff5f56', '#ffbd2e', '#27c93f'].map((color, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                width: { xs: 8, sm: 12 },
                                                height: { xs: 8, sm: 12 },
                                                borderRadius: '50%',
                                                bgcolor: color
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        flexGrow: 1,
                                        textAlign: 'center',
                                        color: 'text.secondary',
                                        fontFamily: 'monospace',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                >
                                    sortify-api.java
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    flexGrow: 1,
                                    bgcolor: '#263238',
                                    color: '#eeffff',
                                    fontFamily: 'monospace',
                                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                                    lineHeight: 1.7,
                                    overflowX: 'auto'
                                }}
                            >
                                <pre style={{ margin: 0 }}>
                                    {`@RestController
@RequestMapping("/api/classify")
public class ClassificationController {

    private final ClassificationService classificationService;
    
    @Autowired
    public ClassificationController(
        ClassificationService classificationService
    ) {
        this.classificationService = classificationService;
    }
    
    @PostMapping
    public ResponseEntity<ClassificationResult> classifyWaste(
        @RequestParam("image") MultipartFile imageFile,
        @AuthenticationPrincipal UserDetails user
    ) {
        // Process the uploaded image
        ClassificationResult result = 
            classificationService.classifyWasteImage(imageFile);
            
        // Save classification to user history
        classificationService.saveToUserHistory(
            result, user.getUsername());
            
        return ResponseEntity.ok(result);
    }
}`}
                                </pre>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Environmental Impact Section - Mobile Responsive */}
            <Box
                sx={{
                    backgroundColor: 'background.paper',
                    py: { xs: 6, sm: 8, md: 10 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/images/pattern-bg.svg)',
                        backgroundSize: 'cover',
                        opacity: 0.03,
                        zIndex: 0
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
                        <Chip
                            icon={<EmojiEventsIcon />}
                            label="Environmental Impact"
                            color="primary"
                            size={isSmallMobile ? "small" : "medium"}
                            sx={{
                                mb: 2,
                                fontWeight: 600,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                        />
                        <Typography
                            variant="h3"
                            component="h2"
                            fontWeight={700}
                            gutterBottom
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                                lineHeight: { xs: 1.2, sm: 1.3 }
                            }}
                        >
                            Together for a Greener Bangladesh
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            paragraph
                            sx={{
                                maxWidth: { xs: '100%', sm: 600, md: 700 },
                                mx: 'auto',
                                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                mb: { xs: 4, sm: 6 },
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            With proper waste sorting, we can reduce landfill waste, increase recycling rates, and protect our environment for future generations.
                        </Typography>
                    </Box>

                    <Grid container spacing={{ xs: 3, sm: 4 }}>
                        {[
                            {
                                title: "Reduce Landfill Waste",
                                description: "Proper waste sorting reduces the amount of recyclable and compostable materials that end up in landfills.",
                                icon: <RecyclingIcon />,
                                gradient: 'linear-gradient(135deg, #43a047, #66bb6a)'
                            },
                            {
                                title: "Conserve Natural Resources",
                                description: "Recycling reduces the need for raw materials, conserving natural resources and reducing pollution from extraction.",
                                icon: <NatureIcon />,
                                gradient: 'linear-gradient(135deg, #2196f3, #03a9f4)'
                            },
                            {
                                title: "Educate Communities",
                                description: "Spreading knowledge about proper waste disposal creates lasting positive change in communities.",
                                icon: <SchoolIcon />,
                                gradient: 'linear-gradient(135deg, #ffc400, #ffab00)'
                            }
                        ].map((item, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: { xs: 3, sm: 4 },
                                        boxShadow: '0 8px 32px rgba(67, 160, 71, 0.1)',
                                        transition: 'transform 0.3s ease',
                                        '&:hover': {
                                            transform: { xs: 'none', sm: 'translateY(-4px)' }
                                        }
                                    }}
                                >
                                    <CardMedia
                                        component="div"
                                        sx={{
                                            pt: { xs: '40%', sm: '56.25%' },
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                background: item.gradient,
                                                opacity: 0.9,
                                            }
                                        }}
                                    >
                                        {React.cloneElement(item.icon, {
                                            sx: {
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                fontSize: { xs: 60, sm: 80 },
                                                color: 'white',
                                                opacity: 0.9
                                            }
                                        })}
                                    </CardMedia>
                                    <CardContent sx={{
                                        flexGrow: 1,
                                        p: { xs: 2, sm: 3 },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            fontWeight={600}
                                            gutterBottom
                                            sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                        >
                                            {item.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section - Mobile Responsive */}
            <Box sx={{ backgroundColor: '#e6f4ea', py: { xs: 6, sm: 8, md: 10 } }}>
                <Container maxWidth="md">
                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 3, sm: 4, md: 6 },
                            borderRadius: { xs: 3, sm: 4 },
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f5fbf7 100%)',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 10px 40px rgba(67, 160, 71, 0.1)'
                        }}
                    >
                        {!isAuthenticated ? (
                            <>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem' },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    Ready to Sort Smarter?
                                </Typography>
                                <Typography
                                    variant="body1"
                                    paragraph
                                    sx={{
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                        maxWidth: { xs: '100%', sm: 600 },
                                        mx: 'auto',
                                        px: { xs: 1, sm: 0 }
                                    }}
                                >
                                    Join Sortify today and become part of Bangladesh's waste management solution.
                                    Get started for free and make a difference with every item you sort.
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 2, sm: 3 }}
                                    justifyContent="center"
                                    sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        color="primary"
                                        component={Link}
                                        to="/register"
                                        sx={{
                                            fontWeight: 600,
                                            px: { xs: 3, sm: 4 },
                                            py: { xs: 1.2, sm: 1.5 },
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' }
                                        }}
                                    >
                                        {isSmallMobile ? 'Get Started Free' : 'Get Started for Free'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        component={Link}
                                        to="/login"
                                        sx={{
                                            fontWeight: 600,
                                            borderWidth: 2,
                                            px: { xs: 3, sm: 4 },
                                            py: { xs: 1.2, sm: 1.5 },
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            '&:hover': { borderWidth: 2 }
                                        }}
                                    >
                                        Sign In
                                    </Button>
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem' },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    Welcome back, {user?.displayName}! 🌟
                                </Typography>
                                <Typography
                                    variant="body1"
                                    paragraph
                                    sx={{
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                        maxWidth: { xs: '100%', sm: 600 },
                                        mx: 'auto',
                                        px: { xs: 1, sm: 0 }
                                    }}
                                >
                                    Ready to continue your eco-warrior journey? Start classifying waste and earn champion points!
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 2, sm: 3 }}
                                    justifyContent="center"
                                    sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        color="primary"
                                        component={Link}
                                        to="/classify"
                                        startIcon={<CloudUploadIcon />}
                                        sx={{
                                            fontWeight: 600,
                                            px: { xs: 3, sm: 4 },
                                            py: { xs: 1.2, sm: 1.5 },
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem' }
                                        }}
                                    >
                                        Start Classifying
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        component={Link}
                                        to="/rewards"
                                        sx={{
                                            fontWeight: 600,
                                            borderWidth: 2,
                                            px: { xs: 3, sm: 4 },
                                            py: { xs: 1.2, sm: 1.5 },
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            '&:hover': { borderWidth: 2 }
                                        }}
                                    >
                                        {isSmallMobile ? `Progress (${user?.rewardsPoints || 0})` : `View Progress (${user?.rewardsPoints || 0} points)`}
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Paper>
                </Container>
            </Box>
        </>
    );
}

